import {InvalidTaxIdentificationNumber, TaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {PersonTypes} from 'Beneficiaries/models/PersonTypes';
import {Email, InvalidEmail} from 'Core/models/Email';
import {EmptyString, isNonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {InvalidPhoneNumber, PhoneNumber} from 'Core/models/PhoneNumber';
import {ContingentBeneficiary, PrimaryBeneficiary} from './PersonTypes';

export type BeneficiaryPerson = {
  id?: string;
  dateOfBirth?: ValidBeneficiaryPersonDateOfBirth | InvalidBeneficiaryPersonDateOfBirth | EmptyString;
  identifier?: TaxIdentificationNumber | StringUnion;
  beneficiaryPercent?: number;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  gender?: string;
  phoneNumber?: PhoneNumber;
  email?: Email;
  personType?: ValidPersonType | EmptyPersonType;
  relationship?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  signatureDate?: Date;
  sourceSystemIdentifier?: StringUnion;
  markedAsDeleted?: boolean;
};
export class ValidBeneficiaryPersonDateOfBirth {
  public static create(value?: string | null) {
    return value
      ? // People can not have a future Date of Birth
        Date.parse(value) > Date.now()
        ? new InvalidBeneficiaryPersonDateOfBirth(new Date(value))
        : new ValidBeneficiaryPersonDateOfBirth(new Date(value))
      : new EmptyString();
  }

  private constructor(public readonly value: Date) {}
}

export class InvalidBeneficiaryPersonDateOfBirth {
  public readonly validationError = 'Date of Birth Must Not be After Today';

  public constructor(public readonly value: Date) {}
}

export class ValidPersonType {
  public static create(id?: number) {
    const personType = PersonTypes.find(pt => pt.id === id);
    return personType ? new ValidPersonType(personType.id, personType.name!) : new EmptyPersonType();
  }

  private constructor(public readonly id: number, public readonly name: string) {}
}

export class EmptyPersonType {
  public constructor(public readonly id?: number, public readonly name?: string) {}
}

export class ValidBeneficiaryPerson {
  public static create(beneficiaryPerson: BeneficiaryPerson, isWalmart: boolean = false) {
    // Email is not required, but must be valid if exist
    const isEmailValidOrNotSpecified = !(beneficiaryPerson.email instanceof InvalidEmail);
    const isIdentifierValidOrSkipValidation = !(beneficiaryPerson.identifier instanceof InvalidTaxIdentificationNumber);
    const isPhoneNumberValidOrNotSpecified = !(beneficiaryPerson.phoneNumber instanceof InvalidPhoneNumber);
    const isDateOfBirthValidOrNotSpecified = !(
      beneficiaryPerson.dateOfBirth instanceof InvalidBeneficiaryPersonDateOfBirth
    );

    let isValid =
      isIdentifierValidOrSkipValidation &&
      isPhoneNumberValidOrNotSpecified &&
      isEmailValidOrNotSpecified &&
      isDateOfBirthValidOrNotSpecified &&
      beneficiaryPerson.personType instanceof ValidPersonType;

    // For walmart beneficiaries, signature date is required
    if (isWalmart) {
      const isBeneficiaryUserType =
        beneficiaryPerson.personType?.id === PrimaryBeneficiary.id ||
        beneficiaryPerson.personType?.id === ContingentBeneficiary.id;
      const validSignatureDateOrNotBeneficiary = !!beneficiaryPerson.signatureDate || !isBeneficiaryUserType;
      const validSourceSystemIdentifierOrNotBeneficiary =
        (!!beneficiaryPerson.sourceSystemIdentifier && isNonEmptyString(beneficiaryPerson.sourceSystemIdentifier)) ||
        !isBeneficiaryUserType;

      isValid = isValid && validSignatureDateOrNotBeneficiary && validSourceSystemIdentifierOrNotBeneficiary;
    }

    return isValid ? new ValidBeneficiaryPerson(beneficiaryPerson) : InvalidBeneficiaryPerson.create(beneficiaryPerson);
  }

  private constructor(public readonly beneficiaryPerson: BeneficiaryPerson) {}
}

export class InvalidBeneficiaryPerson {
  public static create(beneficiaryPerson: BeneficiaryPerson) {
    return new InvalidBeneficiaryPerson(beneficiaryPerson);
  }

  private constructor(public readonly beneficiaryPerson: BeneficiaryPerson) {}
}
